// src/store/batchCreationStore.ts
import { create } from "zustand";
import { useAdminBatchStore, EnrolledStudentForBatch } from "./adminBatchStore";
import { Subscription } from "./billingStore";

const gradeCode = (educationLevel?: string | null) => {
  const normalizedLevel = educationLevel?.trim().toLowerCase().replace(/[\s_-]+/g, "");
  const namedLevels: Record<string, string> = {
    prek: "Pre-K",
    kindergarten: "Kindergarten",
    algebra1: "Algebra1",
    geometry: "Geometry",
    algebra2: "Algebra2",
    precalculus: "Precalculus",
  };
  if (normalizedLevel && namedLevels[normalizedLevel]) return namedLevels[normalizedLevel];

  const numericGrade = educationLevel?.match(/(?:grade\s*-?\s*)?(\d+)/i)?.[1];
  if (numericGrade) return `G${numericGrade}`;

  const wordGrades: Record<string, string> = {
    first: "G1", second: "G2", third: "G3", fourth: "G4",
    fifth: "G5", sixth: "G6", seventh: "G7", eighth: "G8",
    ninth: "G9", tenth: "G10", eleventh: "G11", twelfth: "G12",
  };
  const matchedWord = Object.keys(wordGrades).find((word) => educationLevel?.toLowerCase().includes(word));
  return matchedWord ? wordGrades[matchedWord] : "NA";
};

const batchNamePart = (value: string) => value.trim().replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "");

export const createDefaultBatchName = (
  student: EnrolledStudentForBatch,
  subscription: EnrolledStudentForBatch["subscriptions"][number],
  createdAt = new Date(),
) => {
  const firstName = batchNamePart(student.first_name) || "Student";
  const subjectInitial = subscription.subject_name?.trim().charAt(0).toUpperCase() || "NA";
  const board = batchNamePart(student.board || "COMMON").toUpperCase();
  const month = String(createdAt.getMonth() + 1).padStart(2, "0");

  return `${firstName}_${gradeCode(student.education_level)}_${subjectInitial}_${board}_${month}_${createdAt.getFullYear()}`;
};

interface BatchCreationState {
  // Form state
  selectedStudent: string;
  selectedTutor: string;
  selectedTutorMatchScore: number | null;
  selectedTutorMatchLabel: string | null;
  selectedSubscription: string;
  batchName: string;
  internalNotes: string;
  enrolledStudents: EnrolledStudentForBatch[];
  selectedStudentData: EnrolledStudentForBatch | null;
  isLoadingStudents: boolean;
  isLoading: boolean;
  error: string | null;
  success: boolean;

  // Actions
  setSelectedStudent: (studentId: string) => void;
  setSelectedTutor: (tutorId: string) => void;
  setSelectedTutorMatchInfo: (score: number | null, label: string | null) => void;
  setSelectedSubscription: (subscriptionId: string) => void;
  setBatchName: (batchName: string) => void;
  setInternalNotes: (internalNotes: string) => void;
  regenerateBatchName: () => void;
  setEnrolledStudents: (students: EnrolledStudentForBatch[]) => void;
  setSelectedStudentData: (student: EnrolledStudentForBatch | null) => void;
  setIsLoadingStudents: (isLoading: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setSuccess: (success: boolean) => void;
  resetForm: () => void;

  // Async actions
  fetchEnrolledStudents: () => Promise<void>;
  handleStudentSelection: (studentId: string) => void;
  handleSubmit: (e: React.FormEvent) => Promise<boolean>;
}

export const useBatchCreationStore = create<BatchCreationState>((set, get) => ({
  // Initial state
  selectedStudent: "",
  selectedTutor: "none",
  selectedTutorMatchScore: null,
  selectedTutorMatchLabel: null,
  selectedSubscription: "",
  batchName: "",
  internalNotes: "",
  enrolledStudents: [],
  selectedStudentData: null,
  isLoadingStudents: false,
  isLoading: false,
  error: null,
  success: false,

  // Actions
  setSelectedStudent: (studentId) => set({ selectedStudent: studentId }),
  setSelectedTutor: (tutorId) => set({ selectedTutor: tutorId }),
  setSelectedTutorMatchInfo: (score, label) =>
    set({ selectedTutorMatchScore: score, selectedTutorMatchLabel: label }),
  setSelectedSubscription: (subscriptionId) => {
    const student = get().selectedStudentData;
    const subscription = student?.subscriptions.find((item) => item.id === subscriptionId);
    set({
      selectedSubscription: subscriptionId,
      batchName: student && subscription ? createDefaultBatchName(student, subscription) : "",
    });
  },
  setBatchName: (batchName) => set({ batchName }),
  setInternalNotes: (internalNotes) => set({ internalNotes }),
  regenerateBatchName: () => {
    const student = get().selectedStudentData;
    const subscription = student?.subscriptions.find((item) => item.id === get().selectedSubscription);
    if (student && subscription) {
      set({ batchName: createDefaultBatchName(student, subscription) });
    }
  },
  setEnrolledStudents: (students) => set({ enrolledStudents: students }),
  setSelectedStudentData: (student) => set({ selectedStudentData: student }),
  setIsLoadingStudents: (isLoading) => set({ isLoadingStudents: isLoading }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setSuccess: (success) => set({ success }),
  resetForm: () => set({
    selectedStudent: "",
    selectedTutor: "none",
    selectedTutorMatchScore: null,
    selectedTutorMatchLabel: null,
    selectedSubscription: "",
    batchName: "",
    internalNotes: "",
    selectedStudentData: null,
    error: null,
    success: false
  }),

  // Async actions
  fetchEnrolledStudents: async () => {
    set({ isLoadingStudents: true, error: null });

    try {
      const { getEnrolledStudentsForBatch } = useAdminBatchStore.getState();
      const students = await getEnrolledStudentsForBatch();

      set({
        enrolledStudents: students,
        isLoadingStudents: false
      });
    } catch (error) {
      console.error("Error fetching enrolled students:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to fetch enrolled students",
        isLoadingStudents: false
      });
    }
  },

  handleStudentSelection: (studentId) => {
    const { enrolledStudents } = get();
    const selectedStudent = enrolledStudents.find(student => student.id === studentId);

    set({
      selectedStudent: studentId,
      selectedStudentData: selectedStudent || null,
      selectedSubscription: "", // Reset subscription selection
      batchName: "",
    });
  },

  handleSubmit: async (e) => {
    e.preventDefault();

    const { selectedStudent, selectedSubscription, selectedStudentData, batchName } = get();

    // Validate form
    if (!selectedStudent || !selectedSubscription || !batchName.trim()) {
      set({ error: "Please fill in all required fields" });
      return false;
    }

    if (!selectedStudentData) {
      set({ error: "Invalid student selected", isLoading: false });
      return false;
    }

    set({ isLoading: true, error: null });

    // Get subscription data from the selected student
    const subscription = selectedStudentData.subscriptions.find(sub => sub.id === selectedSubscription);

    if (!subscription) {
      set({ error: "Invalid subscription selected", isLoading: false });
      return false;
    }

    try {
      // Create batch
      const { createBatch } = useAdminBatchStore.getState();
      const result = await createBatch({
        name: batchName.trim(),
        student_id: selectedStudent,
        package_type: subscription.product_type,
        package_name: subscription.product_name,
        default_tutor_id: get().selectedTutor === "none" ? null : get().selectedTutor || null,
        subscription_id: selectedSubscription,
        workflow_id: subscription.workflow_id,
        total_sessions: subscription.sessions_count ?? undefined,
      });

      set({ isLoading: false });

      if (result.success) {
        set({ success: true });
        return true;
      } else {
        set({ error: result.error || "Failed to create batch" });
        return false;
      }
    } catch (error) {
      console.error("Error creating batch:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to create batch",
        isLoading: false
      });
      return false;
    }
  }
}));
